import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  coerceLocale,
  mapAuthoredText,
  pickAuthoredText,
  pickLocalizedText,
  isHiddenProgram,
  type AssignmentKind,
  type AuthoredText,
  planCloneBody,
  programCloneBody,
  resolvePlanTemplate,
  resolveProgramTemplate,
  type NewProgram,
  type Plan,
  type PlanAssigneesPatch,
  type Program,
  type ProgramAssigneesPatch,
} from "@heraion/shared";

import type { ProgramApiFetch } from "../ProgramEditor/types";
import { writeError, type DurableWriteFn } from "../seams";
import { usePrograms } from "../Programs/usePrograms";
import { officialCopyKeys, planKey, programCopyKeys } from "../Programs/usePendingProgramCopies";
import { usePlans } from "../Plans/usePlans";
import { useTemplates, type TemplateReadKind } from "./useTemplates";
import { useFavorites } from "./useFavorites";
import { useFollows } from "./useFollows";
import {
  copyStatus,
  filterLibraryItems,
  inLibraryView,
  isCoachView,
  isFollowable,
  type LibraryItem,
} from "./libraryFilters";

// Pure predicates/types (LibraryFilter, LibraryItem, filterLibraryItems,
// isOfficialProgram, libraryItemName, libraryFavoriteKey, personalCreatedBy)
// live in ./libraryFilters, re-exported below so every existing import of
// them from "./useLibrary" keeps working unchanged.
export {
  filterLibraryItems,
  isFollowable,
  isOfficialProgram,
  isReadOnlyItem,
  libraryItemName,
  libraryFavoriteKey,
  personalCreatedBy,
} from "./libraryFilters";
export type { LibraryFilter, LibraryItem } from "./libraryFilters";

// The Library-browser data layer (redesign track HR §S3): ONE hook that
// composes the existing shared list hooks — usePrograms (SWR + status
// mutations), usePlans, useTemplates — into the browse buckets, and owns
// the item actions (follow / clone) so mobile and web-rnw mount the same
// logic. NO new fetch paths: personal rows come from the same hooks the
// Plan surfaces already use; official rows are the /api/templates set.
// The coach bucket is a role-gated placeholder until the capabilities
// spine (PE-5) lights it up.
//
// LibraryFilter, LibraryItem and the pure item predicates/filter
// (filterLibraryItems, isOfficialProgram, libraryItemName, libraryFavoriteKey,
// personalCreatedBy) moved to ./libraryFilters, re-exported above.

/** #472: the Metria models are in the catalog, next to the programs. */
const LIBRARY_READS: readonly TemplateReadKind[] = ["plan", "program", "model"];

export type UseLibraryOptions = {
  apiFetch: ProgramApiFetch;
  /** Host write seam — mobile queues when offline, web sends only. */
  durableWrite: DurableWriteFn;
  /** The signed-in caller's sub: the "coach" tab discriminant and the gate
   *  for athlete-view (assigned to you) and coach-view (own rows) rows.
   *  `null` (the default) means "not yet known": no personal row matches
   *  once a view is set. Optional for probes that set no view. */
  callerSub?: string | null;
  /** CO-19 — the active view (Experience) this mount shows the library
   *  for. Parallel option to `callerSub`, same shape and same "not yet
   *  known" default: absent/null means no authoredAs filtering (every
   *  personal row shows), never a probe value. Threaded straight into
   *  `filterLibraryItems` (returned below for the host's own calls to it)
   *  and into `cloneItem`'s clone-body builders, so a clone made from
   *  this mount is stamped with the view it was made FROM, never
   *  inherited from the source being cloned (clonePayloads.ts). */
  authoredAs?: Program["authoredAs"] | null;
};

/** #475: [resolved name, model id] an official item's copy answers to. */
const copyKeyArgs = (
  item: Extract<LibraryItem, { source: "official" }>,
): [string, string?] =>
  item.kind === "model" ? [item.name, item.model.id] : [item.template.name];

export function useLibrary(opts: UseLibraryOptions) {
  const { t, i18n } = useTranslation();
  const lang = coerceLocale(i18n.resolvedLanguage);

  const {
    apiFetch,
    durableWrite,
    callerSub = null,
    authoredAs = null,
  } = opts;
  // Before usePrograms: a followed program is bucketed by the follow, not by
  // its author's status (next/coaching.md, "La SPEC del follower" §2).
  const followsState = useFollows({ apiFetch, durableWrite });
  const programsState = usePrograms({
    apiFetch,
    durableWrite,
    followedProgramIds: followsState.followedProgramIds,
  });
  const plansState = usePlans({ apiFetch, durableWrite });
  const templatesState = useTemplates({ apiFetch, kinds: LIBRARY_READS });
  const favoritesState = useFavorites({ apiFetch, durableWrite });

  const [cloningKey, setCloningKey] = useState<string | null>(null);
  const [cloneError, setCloneError] = useState<string | null>(null);
  const [cloneFlash, setCloneFlash] = useState<string | null>(null);
  // Items cloned since the browser mounted — keeps the action disabled
  // across the parent-list refetch race (same guard as TemplatesShelf).
  const [clonedKeys, setClonedKeys] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  // #448: a hidden program is out of the Library, and is no copy either.
  const shownPrograms = useMemo(
    () =>
      programsState.programs.filter(
        (p) => !isHiddenProgram(p.status, followsState.followedProgramIds.has(p.id)),
      ),
    [programsState.programs, followsState.followedProgramIds],
  );

  const items = useMemo<LibraryItem[]>(() => {
    const out: LibraryItem[] = [];
    for (const tpl of templatesState.programTemplates) {
      out.push({
        key: `official/program/${tpl.id}`,
        kind: "program",
        source: "official",
        template: resolveProgramTemplate(tpl, lang),
      });
    }
    for (const model of templatesState.models) {
      out.push({
        key: `official/model/${model.id}`,
        kind: "model",
        source: "official",
        model,
        name: pickLocalizedText(model.name, lang),
        description: pickLocalizedText(model.description, lang),
      });
    }
    for (const tpl of templatesState.planTemplates) {
      out.push({
        key: `official/plan/${tpl.id}`,
        kind: "plan",
        source: "official",
        template: resolvePlanTemplate(tpl, lang),
      });
    }
    for (const program of shownPrograms) {
      out.push({
        key: `personal/program/${program.id}`,
        kind: "program",
        source: "personal",
        program,
      });
    }
    for (const plan of plansState.plans) {
      out.push({
        key: `personal/plan/${plan.id}`,
        kind: "plan",
        source: "personal",
        plan,
      });
    }
    return out;
  }, [
    templatesState.programTemplates,
    templatesState.models,
    templatesState.planTemplates,
    shownPrograms,
    plansState.plans,
    lang,
  ]);

  // #472: what is already the caller's, in this view only (inLibraryView).
  const viewPrograms = useMemo(
    () => shownPrograms.filter((p) => inLibraryView(p, authoredAs)),
    [shownPrograms, authoredAs],
  );

  // Plan names already in the personal lists (cross-session de-dup by
  // resolved name, the TemplatesShelf contract).
  const existingPlanNames = useMemo(
    () => new Set(plansState.plans.map((p) => p.name)),
    [plansState.plans],
  );

  // #477: the caller's programs that are a copy of an official item: same
  // resolved name, or (#472) made from that model, since a copy keeps its
  // origin and a renamed one still counts.
  const programCopies = useCallback(
    (item: Extract<LibraryItem, { source: "official" }>): Program[] => {
      // The one rule (usePendingProgramCopies): same name, or made from the model.
      const wanted = officialCopyKeys(...copyKeyArgs(item));
      return viewPrograms.filter((p) => {
        const have = programCopyKeys(p, lang);
        return wanted.some((k) => have.includes(k));
      });
    },
    // `lang`: the de-dup compares RESOLVED names.
    [viewPrograms, lang],
  );

  /** What «Go to your copy» opens. None for a workout: its copy is a plan. */
  const findCopies = useCallback(
    (item: LibraryItem): Program[] =>
      item.source === "official" && item.kind !== "plan" ? programCopies(item) : [],
    [programCopies],
  );

  // #475: an official item's copy is queued, not listed yet. A workout's
  // copy is a plan, by name.
  const pendingCopyKeys = programsState.pendingCopyKeys;
  const pendingPlanKeys = plansState.pendingCopyKeys;
  const copyQueued = useCallback(
    (item: LibraryItem): boolean =>
      item.source === "official" &&
      (item.kind === "plan"
        ? pendingPlanKeys.has(planKey(item.template.name))
        : officialCopyKeys(...copyKeyArgs(item)).some((k) => pendingCopyKeys.has(k))),
    [pendingCopyKeys, pendingPlanKeys],
  );

  // Drives the official items' «Already yours» state. `clonedKeys` covers
  // a clone the list has not re-read yet.
  const alreadyCloned = useCallback(
    (item: LibraryItem): boolean =>
      item.source === "official" &&
      (clonedKeys.has(item.key) ||
        copyQueued(item) ||
        existingPlanNames.has(item.kind === "model" ? item.name : item.template.name) ||
        programCopies(item).length > 0),
    [programCopies, existingPlanNames, clonedKeys, copyQueued],
  );

  /** Clone any library item into the caller's partition. Templates keep
   *  their resolved name (de-dup above prevents doubles); personal rows
   *  get a localized "(copy)" suffix so the copy is tellable apart.
   *  #472: a model is followed with the program its follow screen built
   *  (`built`); without it nothing is sent. True once the copy is durable. */
  const cloneItem = useCallback(
    async (item: LibraryItem, built?: NewProgram): Promise<boolean> => {
      if (cloningKey) return false;
      if (item.kind === "model" && !built) return false;
      // #475: a copy already queued is not made twice. The coach view
      // makes one per variant, so a model is let through there.
      if (copyQueued(item) && !(item.kind === "model" && isCoachView(authoredAs))) return false;
      setCloningKey(item.key);
      setCloneError(null);
      setCloneFlash(null);
      const copySuffix = t("library.copySuffix", { defaultValue: "(copy)" });
      // A Metria program is FOLLOWED, not cloned: the copy is the
      // mechanism, so it lands already followed (`active`) and shows up in
      // In progress with its Start. ONE place decides this (copyStatus).
      const status = copyStatus(item, authoredAs);
      try {
        let name: AuthoredText | string;
        let result;
        if (item.kind === "model" && built) {
          name = built.name;
          result = await durableWrite({
            method: "POST",
            path: "/api/me/programs",
            // CO-17: the cloner's view, never the model's.
            body: { ...built, status, ...(authoredAs ? { authoredAs } : {}) },
          });
          void programsState.programsChanged();
        } else if (item.kind === "program") {
          const src =
            item.source === "personal" ? item.program : item.template;
          // Rename EVERY language the source carries: a clone of a
          // translated program must not arrive collapsed to one.
          name =
            item.source === "personal"
              ? mapAuthoredText(src.name, (n) => `${n} ${copySuffix}`)
              : src.name;
          result = await durableWrite({
            method: "POST",
            path: "/api/me/programs",
            body: programCloneBody(src, {
              name,
              status,
              // CO-17 — the CLONER's view, never the source's: cloning a
              // coach-authored program from the athlete tab must print
              // "athlete" on the copy, not carry over "coach".
              ...(authoredAs ? { authoredAs } : {}),
            }),
          });
          void programsState.programsChanged();
        } else if (item.kind === "plan") {
          const src = item.source === "personal" ? item.plan : item.template;
          name =
            item.source === "personal"
              ? `${src.name} ${copySuffix}`
              : src.name;
          result = await durableWrite({
            method: "POST",
            path: "/api/me/plans",
            body: planCloneBody(src, {
              name,
              // CO-17 — same non-inheritance rule as the program branch above.
              ...(authoredAs ? { authoredAs } : {}),
            }),
          });
          void plansState.refresh();
        } else return false;
        const err = writeError(result);
        if (err !== null) {
          // Generic localized message to the user; the raw code (e.g.
          // api_error_409) is a breadcrumb, not UI copy.
          console.warn("[library] clone_failed", err.message);
          setCloneError(
            t("library.errClone", {
              defaultValue: "Could not clone. Try again.",
            }),
          );
          return false;
        }
        // A queued clone counts as cloned: it is durable, and telling the
        // athlete otherwise would be the lie DX-2 removes.
        setCloneFlash(
          // PE-21 — resolve before interpolating: i18n accepts any value
          // and a per-locale object would surface as "[object Object]".
          // The word has to match the gesture the athlete made: "cloned"
          // on a Metria program would name the mechanism the card
          // deliberately hides.
          // #311: in the coach view the copy is not followed, it joins the
          // coach's programs to assign, and the flash says so.
          t(
            !isFollowable(item)
              ? "library.catalog.cloned"
              : isCoachView(authoredAs)
                ? "library.addedToPrograms"
                : "library.followed",
            { name: pickAuthoredText(name, lang) ?? "" },
          ),
        );
        setClonedKeys((prev) => new Set(prev).add(item.key));
        // #475: queued, so the list will not show it until the flush.
        if (result.outcome === "queued" && item.source === "official") {
          if (item.kind === "plan") plansState.holdCopy([planKey(item.template.name)]);
          else programsState.holdCopy(officialCopyKeys(...copyKeyArgs(item)));
        }
        return true;
      } finally {
        setCloningKey(null);
      }
    },
    // The deps below name the EXACT members this callback calls; eslint
    // wants the whole `programsState`/`plansState` object, which would
    // re-create the callback whenever any unrelated part of that state
    // changes. Pre-existing, surfaced when packages/ui got a linter.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      cloningKey,
      t,
      durableWrite,
      programsState.programsChanged,
      programsState.holdCopy,
      copyQueued,
      plansState.refresh,
      plansState.holdCopy,
      authoredAs,
    ],
  );

  /** Status moves on the full axis: follow (→active), unfollow
   *  (→archived). The Library owns these since HR-S4 retired the
   *  ProgramsPlanView rows. */
  const setProgramStatus = useCallback(
    async (program: Program, status: Program["status"]): Promise<Error | null> =>
      programsState.setProgramStatus(program.id, status),
    // Exact member listed on purpose — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programsState.setProgramStatus],
  );

  // CO-21 — the standalone Assign sheet's save, one wrapper per kind
  // (mirrors setProgramStatus's own single-member wrapper above): the sheet
  // already knows which kind it's editing (its `item.kind`), so it calls the
  // matching one directly rather than this hook re-deriving it from a
  // LibraryItem.
  const setProgramAssignees = useCallback(
    (program: Program, body: ProgramAssigneesPatch): Promise<Error | null> =>
      programsState.setProgramAssignees(program.id, body),
    // Exact member listed on purpose — see the note above setProgramStatus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programsState.setProgramAssignees],
  );

  const setPlanAssignees = useCallback(
    (plan: Plan, body: PlanAssigneesPatch): Promise<Error | null> =>
      plansState.setPlanAssignees(plan.id, body),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [plansState.setPlanAssignees],
  );

  /** Delete a personal program/plan (management moved here in HR-S4).
   *  Templates are never deletable — they aren't the caller's rows. */
  const deleteItem = useCallback(
    async (item: LibraryItem): Promise<Error | null> => {
      if (item.source !== "personal") return null;
      return item.kind === "program"
        ? programsState.deleteProgram(item.program.id)
        : plansState.deletePlan(item.plan.id);
    },
    // Exact members listed on purpose — see the note above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [programsState.deleteProgram, plansState.deletePlan],
  );

  /** Stop following, then re-read the list the row came from (#377). A
   *  program: once the DELETE has succeeded, this list and every other one
   *  under the session's invalidator (#380); no DELETE, no notice (#434).
   *  A plan: this list. */
  const unfollow = useCallback(
    async (kind: AssignmentKind, itemId: string): Promise<Error | null> => {
      const sends = followsState.sendsUnfollow(kind, itemId);
      const err = await followsState.unfollow(kind, itemId);
      if (kind !== "program") void plansState.refresh();
      else if (sends && err === null) programsState.programsChanged();
      return err;
    },
    // Exact members listed on purpose, see the note above setProgramStatus.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      followsState.sendsUnfollow,
      followsState.unfollow,
      plansState.refresh,
      programsState.programsChanged,
    ],
  );

  /** Drop stale clone feedback (the host clears on tab switch so a
   *  "Cloned X" flash never outlives its context). */
  const clearFeedback = useCallback(() => {
    setCloneFlash(null);
    setCloneError(null);
  }, []);

  return {
    items,
    filterLibraryItems,
    callerSub,
    authoredAs,
    personalStatus: programsState.programsStatus,
    plansStatus: plansState.status,
    templatesStatus: templatesState.status,
    cloningKey,
    cloneError,
    cloneFlash,
    alreadyCloned,
    copyQueued,
    findCopies,
    cloneItem,
    setProgramStatus,
    setProgramAssignees,
    setPlanAssignees,
    deleteItem,
    clearFeedback,
    favoriteKeys: favoritesState.favoriteKeys,
    favoritesStatus: favoritesState.favoritesStatus,
    favoritePendingKey: favoritesState.favoritePendingKey,
    toggleFavorite: favoritesState.toggleFavorite,
    // What the caller follows: read-only rows (isReadOnlyItem) and the one
    // write they allow, "stop following".
    followedKeys: followsState.followedKeys,
    followsStatus: followsState.followsStatus,
    unfollow,
  };
}

export type LibraryState = ReturnType<typeof useLibrary>;
